import {
	DocumentReference,
	Maybe,
	Origin,
	RegulatoryDocument,
	WorkflowStatus,
} from 'types';

import _ from 'lodash';
import {TFunction} from 'i18next';
import {BasicDocFields} from './components/RegulatoryDocumentsForm/RegulatoryDocumentsForm.types';
import {KEY_FOR_NO_SELECTION} from './components/RegulatoryDocumentsForm/PredecessorOrSuccessor';
import {convertToRef, mapToRef, PossibleRef} from 'helpers';
import {DocumentSourceEnum} from 'features/Regulations/RegulationDetail/RegulationDetail.types';

type PredecessorOrSuccessor = RegulatoryDocument['predecessor' | 'successor'];

// For existing child regulation ( Modified), parent (Finalized) can't be edited.
// ET164-2886 - Modified regulation can be edited by all Vkos and modified approval regulation can be edited by only reviwer / reapprovingVKO
export const isRegDocEditDisabled = (
	regDoc: Partial<RegulatoryDocument> | any,
	isVko: boolean,
	encodedUserId: string,
) => {
	const status = regDoc?.workflow?.status;
	const reapprovingVKO = regDoc?.workflow?.reapprovingVKO?.id;
	const clearingVKO = regDoc?.workflow?.clearingVKO?.at(0)?.id;

	// Only the clearing VKO can edit when status is 'Clearing'
	if (status === WorkflowStatus.Clearing) {
		return encodedUserId !== clearingVKO;
	}

	// If the user is the reapprovingVKO, always allowed to make edit both version and metadata
	if (!isSystemUser(reapprovingVKO) && encodedUserId === reapprovingVKO) {
		return false;
	}

	// If the status is Approval Modified, all users including VKos cannot edit both versiondata and metadata
	if (
		!isSystemUser(reapprovingVKO) &&
		status === WorkflowStatus.Modified &&
		reapprovingVKO !== encodedUserId
	) {
		return true;
	}

	// If the status is Modified, all VKOs can edit both versiondata and metadata
	if (
		isVko &&
		isSystemUser(reapprovingVKO) &&
		status === WorkflowStatus.Modified
	) {
		return false;
	}

	// If status is 'Modified', and user is not a VKO, editing is disabled. All Vkos can edit if the status is in modified
	if (status === WorkflowStatus.Modified && !isVko) {
		return true;
	}

	//  If the status is Approving-modified, everyone else is disabled (except the reapprovingVKO, which is handled in 2nd step.)
	if (status === 'APPROVING_MODIFIED') {
		return true;
	}

	// If status is 'Finalized' and the regulation has children, editing is disabled
	if (status === WorkflowStatus.Finalized && regDoc?.childrenRefs?.length > 0) {
		return true;
	}

	return false;
};

export const isRegDocEditVersionDataDisabled = (
	regDoc: Partial<RegulatoryDocument> | any,
	isAdmin: boolean,
	isVko: boolean,
	encodedUserId: string,
) => {
	if (isAdmin) {
		return false;
	}
	return isRegDocEditDisabled(regDoc, isVko, encodedUserId);
};

export const isPogisDocument = (
	regulatory: Partial<RegulatoryDocument> | any,
): boolean => {
	return (
		regulatory?.documentSource?.name?.toLowerCase() ===
		DocumentSourceEnum.Pogis.toLowerCase()
	);
};

export const getDisabledEditionForMOdifiedVersionTooltip = (
	regulatory: Partial<RegulatoryDocument> | any,
	t: TFunction,
) => {
	return regulatory?.workflow?.status === WorkflowStatus.Finalized &&
		regulatory?.childrenRefs?.length !== 0
		? t('DisabledEditModifiedExist')
		: '';
};

export const getCorrectStatusName = (
	regulatoryDocumentDetails: any,
	t: TFunction,
	getWorkflowStatus: any,
) => {
	const status = regulatoryDocumentDetails?.workflow?.status;
	if (status !== WorkflowStatus.Modified) {
		return getWorkflowStatus(regulatoryDocumentDetails?.workflow?.status);
	}

	if (
		isApprovalModified(regulatoryDocumentDetails?.workflow?.reapprovingVKO?.id)
	) {
		return getWorkflowStatus('APPROVING_MODIFIED');
	}

	return regulatoryDocumentDetails?.workflow?.message
		? `${getWorkflowStatus(regulatoryDocumentDetails?.workflow?.status)} (${t(
				'Message',
		  )}: ${regulatoryDocumentDetails?.workflow?.message})`
		: getWorkflowStatus(regulatoryDocumentDetails?.workflow?.status);
};

export const isSystemUser = (userId: string) => {
	if (userId) {
		const id = window.atob(userId);
		return id === 'User:00000000-0000-0000-0000-000000000000';
	}
	return false;
};

export const isApprovalModified = (reapproverId: string) => {
	if (reapproverId) {
		return !isSystemUser(reapproverId);
	}
	return false;
};

export const getValueForPredecessorOrSuccessor = (
	optionForPredecessorOrSuccessor: BasicDocFields | PredecessorOrSuccessor,
): PredecessorOrSuccessor | PossibleRef => {
	if (optionForPredecessorOrSuccessor?.id === KEY_FOR_NO_SELECTION) return null;
	return convertToRef(optionForPredecessorOrSuccessor);
};

export type DocumentReferenceType = {
	referenceDocumentType: 'REGULATION' | 'REGULATORY_DOCUMENT';
	documentReferenceGroup: {
		regulationReference?: {id: string} | null;
		regulatoryDocumentReferences: {id: string}[];
		selectAllRegulatoryDocuments: boolean;
	};
	reference: {id: string};
	selectAllRegulatoryDocuments: boolean;
};

export const mapDocumentReferences = (
	documentReferences?: Maybe<DocumentReference[]>,
): DocumentReferenceType[] => {
	if (!documentReferences) {
		return [];
	}

	return documentReferences.map((r: any) => {
		return {
			referenceDocumentType: r.referenceDocumentType,
			reference: {id: r.reference.id},
			documentReferenceGroup: {
				regulationReference: r.documentReferenceGroup?.regulationReference
					? {id: r.documentReferenceGroup.regulationReference.id}
					: null,

				regulatoryDocumentReferences:
					r.documentReferenceGroup?.regulatoryDocumentReferences?.map(
						(ref: any) => ({id: ref.id}),
					) ?? [],

				selectAllRegulatoryDocuments:
					r.documentReferenceGroup?.selectAllRegulatoryDocuments ?? false,
			},
			referencedByDocuments:
				r.referencedByDocuments?.map((doc: any) => ({id: doc.id})) ?? [],
			selectAllRegulatoryDocuments: r.selectAllRegulatoryDocuments || false,
		};
	});
};

export const mapWorkflowUserRefs = (workflow: any) => ({
	clearingVKO: workflow?.clearingVKO ? mapToRef(workflow.clearingVKO) : undefined,
	processingAuthor: workflow?.processingAuthor ? convertToRef(workflow.processingAuthor) : undefined,
	reapprovingAuthor: workflow?.reapprovingAuthor ? convertToRef(workflow.reapprovingAuthor) : undefined,
	processingVKO: workflow?.processingVKO ? convertToRef(workflow.processingVKO) : undefined,
	processingVKOExternal: workflow?.processingVKOExternal ? convertToRef(workflow.processingVKOExternal) : undefined,
	reapprovingVKO: workflow?.reapprovingVKO ? convertToRef(workflow.reapprovingVKO) : undefined,
});

export const getContentFinalizedInput = (
	data: any,
	regulatoryDocument: any,
	regulationId: string,
	showTopicManagementSummaryState: boolean,
	isVko: boolean,
	rule: any,
) => {
	return {
		summary: data.summary,
		regulationRef: regulationId ? {id: regulationId} : undefined,
		name: data.name,
		status: data.status,
		topicManagementSummary:
			data.topicManagementSummary?.quarter &&
			data.topicManagementSummary?.year &&
			showTopicManagementSummaryState
				? {
						quarter: data.topicManagementSummary?.quarter,
						year: data.topicManagementSummary?.year,
				  }
				: null,
		predecessorRef: getValueForPredecessorOrSuccessor(data.predecessor),
		successorRef: getValueForPredecessorOrSuccessor(data.successor),
		finalVersionRef: convertToRef(regulatoryDocument),
		documentSourceRef: convertToRef(data.documentSource),
		origin: isVko ? Origin.Internal : Origin.External,
		workflow: {
			status: WorkflowStatus.Modified,
		},
		dateEffective: data.dateEffective,
		dateExpiration: data.dateExpiration,
		willBeAnonymized: rule?.anonymize || false,
		deletionPeriod: rule?.deletionPeriod || 0,
		documentReferences: mapDocumentReferences(data.documentReferences),
	};
};
